import React, { useState, useEffect, useRef } from "react";
import {
  callErrorToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import {
  Location,
  convertLocationToCreate,
  convertLocationToUpdate,
  validate,
} from "../../../Models/Location.ts";
import { LocationsService as RegistryLocationsService } from "../../../services/registry/locations.ts";
import { LocationsService as LogisticLocationsService } from "../../../services/logistic/locations.ts";
import { LocationsService as ShipmentLocationsService } from "../../../services/shipment/locations.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig.ts";
import { LocationHandlerProviderProps } from "./types.ts";
import { LocationHandlerContext } from "./context.ts";

/* ===========================================================================
 * SERVICE REGISTRY
 * =========================================================================== */

const services = {
  logistic: { locationsService: new LogisticLocationsService() },
  registry: { locationsService: new RegistryLocationsService() },
  shipment: { locationsService: new ShipmentLocationsService() },
};

/* ===========================================================================
 * PROVIDER: LocationHandlerProvider
 * =========================================================================== */

/**
 * @component LocationHandlerProvider
 *
 * Provides CRUD logic, validation, loading/error state and modal management
 * for Location entities. Works seamlessly with both registry-service and
 * logistic-service depending on the `service` prop.
 *
 * Responsibilities:
 * - Load a location by ID (`getLocation`)
 * - Create or edit a location (`saveLocation`)
 * - Validate before persistence
 * - Handle autosave or manual save modes
 * - Track loader and error state
 * - Provide modal configuration utilities
 */
const LocationHandlerProvider: React.FC<LocationHandlerProviderProps> = ({
  id,
  children,
  action,
  callback,
  service = "registry",
  defaultValue = null,
  autosave = false,
}) => {
  const locationsService = services[service].locationsService;

  /** Local state */
  const [data, setData] = useState<Partial<Location> | null>(defaultValue);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] = useState<ErrorResponse<Location> | null>(null);
  const prevError = useRef<ErrorResponse<Location> | null>(null);

  /** Modal configuration */
  const [modalConfig, setModalConfig] = useState<ModalConfig>(
    createModalConfig()
  );

  /* ---------------------------------------------------------------------------
   * GET LOCATION
   * ------------------------------------------------------------------------- */

  /**
   * Fetches a Location by ID and updates local state.
   *
   * @async
   * @param {string} id - Location identifier.
   */
  const getLocation = async (id: string): Promise<void> => {
    setLoader(true);
    try {
      const res = await locationsService.get(id);

      if (data?.id !== id && action === "clone") {
        const location: Partial<Location> = { ...res };
        delete location.id;
        saveLocation(location as Location, null, true);
        return;
      }
      setData(res);
    } catch (err) {
      setError(err as ErrorResponse<Location>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * CREATE
   * ------------------------------------------------------------------------- */

  const create = async (location: Location): Promise<Location> => {
    const payload = convertLocationToCreate(location);
    const created = await locationsService.create(payload);
    return created;
  };

  /* ---------------------------------------------------------------------------
   * EDIT
   * ------------------------------------------------------------------------- */

  const edit = async (location: Location): Promise<Location> => {
    const payload = convertLocationToUpdate(location);
    await locationsService.edit(payload);
    const refreshed = await locationsService.get(location.id);
    return refreshed;
  };

  /* ---------------------------------------------------------------------------
   * SAVE LOCATION
   * ------------------------------------------------------------------------- */

  /**
   * Validates and persists (create/edit) a Location entity.
   *
   * @param location Full Location object
   * @param property Optional field to clear field-level validation errors
   * @param save Whether to persist (true) or update local state only (false)
   * @param execCallback Whether callback should run after save
   */
  const saveLocation = async (
    location: Location,
    property: keyof Location | null,
    save = true,
    execCallback = true
  ): Promise<Location | void> => {
    const cleaned = cleanObject(location) as Location;

    // 1. Remove field-level errors
    if (property) {
      removeError(property, error, prevError, setError);
    }

    // 2. If no persistence is required → update local state only
    if (!save) {
      setData(cleaned);
      return cleaned;
    }

    // 3. Validation
    const validation = validate(cleaned);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      return;
    }

    try {
      setLoader(true);

      let result: Location;

      // 4. CREATE
      if (!cleaned.id) {
        result = await create(cleaned);
      }
      // 5. EDIT
      else {
        result = await edit(cleaned);
      }

      const newData = { ...cleaned, ...result };

      setData(newData);

      if (callback && execCallback) {
        callback(newData);
      }

      setModalConfig(createModalConfig());
      return newData;
    } catch (err) {
      setError(err as ErrorResponse<Location>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  useEffect(() => {
    if (id && data?.id !== id) {
      getLocation(id);
    }
  }, [id]);

  useEffect(() => {
    if (error) callErrorToast(error);
  }, [error]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  return (
    <LocationHandlerContext.Provider
      value={{
        modalConfig,
        action,
        autosave,
        data,
        loader,
        error,
        callback,
        saveLocation,
        getLocation,
        setModalConfig,
      }}
    >
      {children}
    </LocationHandlerContext.Provider>
  );
};

export { LocationHandlerProvider };
